// Right-hand polygon panel: connected terms ordered by votes, vote buttons,
// suggest/attach entry points, admin polygon actions.
import type { ConnectionView } from '../shared/types';
import { api, ApiError } from './api';
import { loadPayload, refreshAnnotated, refreshPendingCount } from './data';
import { $, el } from './dom';
import { state } from './state';
import { openSuggestDialog } from './suggest';
import { openTermDetail } from './termsUi';
import { toast } from './toast';
import { getTurnstileToken } from './turnstile';

export function initPanel(): void {
  state.addEventListener('payload', render);
  state.addEventListener('selection', render);
  state.addEventListener('admin', render);

  $('#btnSuggest').addEventListener('click', () => {
    const poly = state.selectedPolygon;
    if (poly) openSuggestDialog(poly.id);
  });
  $('#btnAttach').addEventListener('click', () => {
    const poly = state.selectedPolygon;
    if (poly) openAttachDialog(poly.id);
  });
  $('#btnDeletePolygon').addEventListener('click', () => {
    const poly = state.selectedPolygon;
    if (!poly || !confirm(`Delete polygon #${poly.id} and all its term connections?`)) return;
    api.admin
      .deletePolygon(poly.id)
      .then(async () => {
        toast('Polygon deleted', 'ok');
        state.selectPolygon(null);
        await loadPayload();
        void refreshAnnotated();
        void refreshPendingCount();
      })
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'delete failed', 'err'));
  });
  $('#btnEditNote').addEventListener('click', () => {
    const poly = state.selectedPolygon;
    if (!poly) return;
    const note = prompt('Note for this polygon (empty to clear):', poly.note ?? '');
    if (note === null) return;
    api.admin
      .updatePolygon(poly.id, { note: note.trim() === '' ? null : note.trim() })
      .then(() => loadPayload())
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'saving note failed', 'err'));
  });

  initAttachDialog();
  render();
}

function render(): void {
  const empty = $<HTMLElement>('#panelEmpty');
  const content = $<HTMLElement>('#panelContent');
  const poly = state.selectedPolygon;
  if (!poly) {
    empty.hidden = false;
    content.hidden = true;
    return;
  }
  empty.hidden = true;
  content.hidden = false;

  const termCount = poly.connections.length;
  $('#panelTitle').textContent = `Polygon #${poly.id} — ${termCount} term${termCount === 1 ? '' : 's'}`;
  const noteEl = $<HTMLElement>('#panelNote');
  noteEl.hidden = !poly.note;
  noteEl.textContent = poly.note ?? '';

  const list = $('#connectionList');
  list.textContent = '';
  if (termCount === 0) {
    const li = el('li', 'dim', 'No terms connected yet — be the first to suggest one.');
    li.style.background = 'none';
    li.style.border = 'none';
    list.appendChild(li);
  }
  for (const conn of poly.connections) list.appendChild(connectionRow(conn));
}

function connectionRow(conn: ConnectionView): HTMLLIElement {
  const li = el('li', 'connection');

  const score = el('span', 'score', conn.score > 0 ? `+${conn.score}` : String(conn.score));
  if (conn.score > 0) score.classList.add('pos');
  if (conn.score < 0) score.classList.add('neg');
  li.appendChild(score);

  const pending = conn.status === 'pending';
  if (!pending) {
    const votes = el('div', 'votes');
    const up = el('button', `vote up${conn.my_vote === 1 ? ' mine' : ''}`, '▲');
    up.title = conn.my_vote === 1 ? 'Remove your upvote' : 'This term is accurate';
    const down = el('button', `vote down${conn.my_vote === -1 ? ' mine' : ''}`, '▼');
    down.title = conn.my_vote === -1 ? 'Remove your downvote' : 'This term is inaccurate';
    up.addEventListener('click', () => void castVote(conn, 1, up, down));
    down.addEventListener('click', () => void castVote(conn, -1, up, down));
    votes.append(up, down);
    li.appendChild(votes);
  }

  const link = el('button', 'term-link', conn.text);
  link.title = 'Show term details';
  link.addEventListener('click', () => void openTermDetail(conn.term_id));
  li.appendChild(link);

  li.appendChild(el('span', 'counts', `▲${conn.up} ▼${conn.down}`));
  if (pending) li.appendChild(el('span', 'chip pending', 'pending review'));

  if (state.admin) {
    const detach = el('button', 'ghost danger', '✕');
    detach.title = 'Remove this term connection';
    detach.addEventListener('click', () => {
      if (!confirm(`Disconnect “${conn.text}” from this polygon?`)) return;
      api.admin
        .deleteConnection(conn.id)
        .then(async () => {
          await loadPayload();
          void refreshPendingCount();
        })
        .catch((err: unknown) => toast(err instanceof Error ? err.message : 'failed', 'err'));
    });
    li.appendChild(detach);
  }
  return li;
}

async function castVote(
  conn: ConnectionView,
  value: 1 | -1,
  up: HTMLButtonElement,
  down: HTMLButtonElement,
): Promise<void> {
  up.disabled = down.disabled = true;
  try {
    const token = await getTurnstileToken('vote');
    if (!token) return;
    const result = await api.vote(conn.id, value, token);
    conn.up = result.up;
    conn.down = result.down;
    conn.score = result.score;
    conn.my_vote = result.my_vote;
    const poly = state.payload?.polygons.find((p) => p.connections.some((c) => c.id === conn.id));
    poly?.connections.sort((a, b) => b.score - a.score || b.up - a.up || a.id - b.id);
    state.emit('payload');
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'vote failed', 'err');
  } finally {
    up.disabled = down.disabled = false;
  }
}

// ------------------------------------------------- admin attach-term dialog

function initAttachDialog(): void {
  const dialog = $<HTMLDialogElement>('#attachDialog');
  const search = $<HTMLInputElement>('#attachSearch');
  let polygonId: number | null = null;

  const renderResults = async () => {
    const q = search.value.trim();
    const results = $('#attachResults');
    let terms;
    try {
      terms = await api.admin.searchTerms(q);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'search failed', 'err');
      return;
    }
    results.textContent = '';
    for (const term of terms.slice(0, 30)) {
      const li = el('li');
      const name = el('button', 'term-name', term.text);
      name.addEventListener('click', () => {
        if (polygonId === null) return;
        api.admin
          .attachTerm(polygonId, term.id)
          .then(async () => {
            toast(`Attached “${term.text}”`, 'ok');
            dialog.close();
            await loadPayload();
          })
          .catch((err: unknown) => toast(err instanceof Error ? err.message : 'attach failed', 'err'));
      });
      li.appendChild(name);
      if (term.synonyms.length) li.appendChild(el('span', 'syns', term.synonyms.join(', ')));
      if (term.status === 'pending') li.appendChild(el('span', 'chip pending', 'pending'));
      results.appendChild(li);
    }
    if (terms.length === 0) results.appendChild(el('li', 'dim', 'No terms found — create one via the Terms dialog.'));
  };

  let timer: number | undefined;
  search.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void renderResults(), 200);
  });

  attachOpen = (id: number) => {
    polygonId = id;
    search.value = '';
    dialog.showModal();
    void renderResults();
  };
}

let attachOpen: ((polygonId: number) => void) | null = null;

function openAttachDialog(polygonId: number): void {
  attachOpen?.(polygonId);
}
